/**
 * The M2 bridge's rock deltas (M3 plan §7 item 6, slice R22i): M2's mining names rocks by `FieldLayout.id`, which for
 * streamed rocks is `rockIdOf(key)` (stable across sessions, R22i), and the Reach names them by key in
 * `state.deltas[system].rocks` (remaining fraction, 0 = gone), which the streamer applies on every stream-in.
 *
 * - `syncRockDeltas`: once `sim.fieldVersion` moves (M2 depleted, fractured or burst a rock), every removed rock of the
 *   live `sim.field` gets a 0 delta by its key. Idle: one comparison, allocation-free (D-014).
 * - `writeRockFractions`: M2's partly mined rocks (`RockDeltas.remaining`, tonnes by id) as fractions of their full
 *   ore mass (`rockOreMassT`: graded by the system's field mix lookup when `mix.gradeMassMult` is on, MC-a6), for a
 *   save or a system exit (rare: may allocate).
 * - `leavingRockDeltas`: M2's rock deltas for leaving a system: fractured parents stay removed and their fragments are
 *   dropped (they would otherwise sit at the same coordinates in the next system).
 */
import { WARE_COUNT, type MiningConfig } from '../../data/m2Schemas'
import { rockMassT, rockOreMassT } from '../../mining/composition'
import { setRockRemaining } from '../../reach/state/deltas'
import type { ReachState } from '../../reach/types'
import type { Sim } from '../../sim'
import type { FieldMixAt, RockDeltas } from '../../space/contracts'
import { fieldIndexOf, type FieldLayout } from '../../world/field'

/** What `syncRockDeltas` last saw. */
export interface RockDeltaSync {
  version: number
}

export function createRockDeltaSync(sim: Pick<Sim, 'fieldVersion'>): RockDeltaSync {
  return { version: sim.fieldVersion }
}

/**
 * After `sim.fieldVersion` moved: writes a 0 delta (by `keys[i]`, parallel to `sim.field`) for every rock of the live
 * field in `sim.removedRocks`. Returns how many deltas changed. Allocation-free while the version is unchanged.
 */
export function syncRockDeltas(
  sync: RockDeltaSync,
  sim: Pick<Sim, 'fieldVersion' | 'field' | 'removedRocks'>,
  keys: readonly string[],
  state: ReachState,
  systemId: string,
): number {
  if (sim.fieldVersion === sync.version) return 0
  sync.version = sim.fieldVersion
  const field = sim.field
  const removed = sim.removedRocks
  if (field === null || removed.size === 0 || keys.length !== field.count) return 0
  let changed = 0
  for (let i = 0; i < field.count; i++) {
    if (removed.has(field.id[i]) && setRockRemaining(state, systemId, keys[i], 0)) changed++
  }
  return changed
}

/** The left system's grade source: its field mix lookup (D-495) and M2's `mining.json mix`. */
export interface RockGrades {
  readonly mixAt: FieldMixAt
  readonly mix: MiningConfig['mix']
}

/**
 * Writes M2's partly mined rocks that are in `field` as remaining fractions (tonnes over the full mass) by key, and a
 * 0 for its removed ones. Returns how many deltas changed. The full mass is the rock's ore mass, which M2 grades by its
 * field when `mix.gradeMassMult` is on (`rockOreMassT`, MC-a6); without `grades` it is the ungraded `rockMassT`.
 */
export function writeRockFractions(
  deltas: Pick<RockDeltas, 'remaining' | 'removed'>,
  field: FieldLayout | null,
  keys: readonly string[],
  massPerCubicMetre: number,
  state: ReachState,
  systemId: string,
  grades: RockGrades | null = null,
): number {
  if (field === null || keys.length !== field.count) return 0
  let changed = 0
  const scratch = grades === null ? null : new Float32Array(WARE_COUNT)
  for (const [id, tonnes] of deltas.remaining) {
    const i = fieldIndexOf(field, id)
    if (i < 0) continue
    const full =
      grades === null || scratch === null
        ? rockMassT(field.radius[i], { massPerCubicMetre })
        : rockOreMassT(field.radius[i], { massPerCubicMetre, mix: grades.mix }, grades.mixAt(field.x[i], field.y[i], field.z[i], scratch))
    if (full > 0 && setRockRemaining(state, systemId, keys[i], tonnes / full)) changed++
  }
  for (const id of deltas.removed) {
    const i = fieldIndexOf(field, id)
    if (i >= 0 && setRockRemaining(state, systemId, keys[i], 0)) changed++
  }
  return changed
}

/** M2's rock deltas for leaving a system: fractured parents become removed rocks, and no fragments are kept. */
export function leavingRockDeltas(deltas: RockDeltas): RockDeltas {
  const removed = new Set(deltas.removed)
  for (const f of deltas.fractured) removed.add(f.parent)
  return {
    remaining: deltas.remaining.filter(([id]) => !removed.has(id)),
    removed: [...removed].sort((a, b) => a - b),
    fractured: [],
  }
}
